/**
 * Gelişmiş Arama ve Filtreleme Sayfası
 * Lig, oyuncu, turnuva arama ve filtreleme
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { 
  Search, Filter, Trophy, Users, Award, Gamepad2, 
  Calendar, TrendingUp, X, ChevronDown, Loader2,
  User, Crown, Target
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { cn } from "../lib/utils";

// Oyun türleri
const GAME_TYPES = [
  { id: "101_okey", name: "101 Okey" },
  { id: "batak", name: "Batak" },
  { id: "pisti", name: "Pişti" },
  { id: "king", name: "King" },
  { id: "rummy", name: "Rummy" },
  { id: "tavla", name: "Tavla" },
];

// Sıralama seçenekleri
const SORT_OPTIONS = {
  leagues: [
    { value: "name_asc", label: "İsim (A-Z)" },
    { value: "name_desc", label: "İsim (Z-A)" },
    { value: "players_desc", label: "Oyuncu Sayısı (Çok-Az)" },
    { value: "players_asc", label: "Oyuncu Sayısı (Az-Çok)" },
    { value: "created_desc", label: "En Yeni" },
    { value: "created_asc", label: "En Eski" },
  ],
  players: [
    { value: "name_asc", label: "İsim (A-Z)" },
    { value: "win_rate_desc", label: "Kazanma Oranı (Yüksek-Düşük)" },
    { value: "leagues_desc", label: "Lig Sayısı (Çok-Az)" },
    { value: "badges_desc", label: "Rozet Sayısı (Çok-Az)" },
  ],
  tournaments: [
    { value: "created_desc", label: "En Yeni" },
    { value: "created_asc", label: "En Eski" },
    { value: "players_desc", label: "Katılımcı (Çok-Az)" },
  ]
};

export default function SearchPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("leagues"); // leagues, players, tournaments
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ leagues: [], players: [], tournaments: [] });
  
  // Filtreler
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    // Lig filtreleri
    gameType: "",
    minPlayers: "",
    maxPlayers: "",
    onlineEnabled: null,
    // Oyuncu filtreleri
    minWinRate: "",
    minLeagues: "",
    hasBadges: null,
    // Turnuva filtreleri
    tournamentType: "",
    tournamentStatus: "",
    // Ortak
    sortBy: "name_asc"
  });

  // Arama fonksiyonu
  const performSearch = useCallback(async () => {
    if (!searchQuery.trim() && !filters.gameType && !filters.tournamentType) {
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append("q", searchQuery.trim());
      params.append("type", activeTab);
      
      // Filtreleri ekle
      if (filters.gameType) params.append("game_type", filters.gameType);
      if (filters.minPlayers) params.append("min_players", filters.minPlayers);
      if (filters.maxPlayers) params.append("max_players", filters.maxPlayers);
      if (filters.onlineEnabled !== null) params.append("online_enabled", filters.onlineEnabled);
      if (filters.minWinRate) params.append("min_win_rate", filters.minWinRate);
      if (filters.minLeagues) params.append("min_leagues", filters.minLeagues);
      if (filters.tournamentType) params.append("tournament_type", filters.tournamentType);
      if (filters.tournamentStatus) params.append("status", filters.tournamentStatus);
      if (filters.sortBy) params.append("sort", filters.sortBy);

      const response = await api.search(params.toString());
      setResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeTab, filters]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2 || filters.gameType || filters.tournamentType) {
        performSearch();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, filters, performSearch]);

  // Tab değişince ara
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      performSearch();
    }
  }, [activeTab]);

  const clearFilters = () => {
    setFilters({
      gameType: "",
      minPlayers: "",
      maxPlayers: "",
      onlineEnabled: null,
      minWinRate: "",
      minLeagues: "",
      hasBadges: null,
      tournamentType: "",
      tournamentStatus: "",
      sortBy: "name_asc"
    });
  };

  const hasActiveFilters = filters.gameType || filters.minPlayers || filters.maxPlayers || 
    filters.onlineEnabled !== null || filters.minWinRate || filters.minLeagues || 
    filters.tournamentType || filters.tournamentStatus;

  return (
    <div className="min-h-screen bg-[#0B1622]" data-testid="search-page">
      {/* Header */}
      <div className="bg-[#050E2E] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
            <Search className="w-6 h-6 inline mr-2 text-[#4DFFFF]" />
            Arama
          </h1>

          {/* Arama kutusu */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Lig, oyuncu veya turnuva ara..."
                className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className={cn(
                "h-12 border-white/20",
                showFilters ? "bg-[#4DFFFF]/20 text-[#4DFFFF] border-[#4DFFFF]" : "text-white"
              )}
              data-testid="filter-toggle"
            >
              <Filter className="w-5 h-5 mr-2" />
              Filtrele
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 rounded-full bg-[#4DFFFF]" />
              )}
            </Button>
          </div>

          {/* Tab seçici */}
          <div className="flex gap-2 mt-4">
            {[
              { id: "leagues", label: "Ligler", icon: Trophy },
              { id: "players", label: "Oyuncular", icon: Users },
              { id: "tournaments", label: "Turnuvalar", icon: Award },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  activeTab === id 
                    ? "bg-[#4DFFFF] text-[#050E2E]" 
                    : "text-white/70 hover:text-white hover:bg-white/10"
                )}
                data-testid={`tab-${id}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filtreler */}
      {showFilters && (
        <div className="bg-[#0A1635] border-b border-white/10 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium">Filtreler</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-[#4DFFFF] hover:underline"
                >
                  Filtreleri Temizle
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Lig filtreleri */}
              {activeTab === "leagues" && (
                <>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Oyun Türü</label>
                    <select
                      value={filters.gameType}
                      onChange={(e) => setFilters(f => ({ ...f, gameType: e.target.value }))}
                      className="w-full h-10 rounded-lg bg-white/10 border border-white/20 text-white px-3"
                      data-testid="filter-game-type"
                    >
                      <option value="">Tümü</option>
                      {GAME_TYPES.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Min Oyuncu</label>
                    <Input
                      type="number"
                      min="1"
                      value={filters.minPlayers}
                      onChange={(e) => setFilters(f => ({ ...f, minPlayers: e.target.value }))}
                      className="h-10 bg-white/10 border-white/20 text-white"
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Max Oyuncu</label>
                    <Input
                      type="number"
                      min="1"
                      value={filters.maxPlayers}
                      onChange={(e) => setFilters(f => ({ ...f, maxPlayers: e.target.value }))}
                      className="h-10 bg-white/10 border-white/20 text-white"
                      placeholder="20"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Online Oyun</label>
                    <select
                      value={filters.onlineEnabled === null ? "" : filters.onlineEnabled.toString()}
                      onChange={(e) => setFilters(f => ({ 
                        ...f, 
                        onlineEnabled: e.target.value === "" ? null : e.target.value === "true" 
                      }))}
                      className="w-full h-10 rounded-lg bg-white/10 border border-white/20 text-white px-3"
                    >
                      <option value="">Tümü</option>
                      <option value="true">Aktif</option>
                      <option value="false">Pasif</option>
                    </select>
                  </div>
                </>
              )}

              {/* Oyuncu filtreleri */}
              {activeTab === "players" && (
                <>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Min Kazanma Oranı (%)</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={filters.minWinRate}
                      onChange={(e) => setFilters(f => ({ ...f, minWinRate: e.target.value }))}
                      className="h-10 bg-white/10 border-white/20 text-white"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Min Lig Sayısı</label>
                    <Input
                      type="number"
                      min="0"
                      value={filters.minLeagues}
                      onChange={(e) => setFilters(f => ({ ...f, minLeagues: e.target.value }))}
                      className="h-10 bg-white/10 border-white/20 text-white"
                      placeholder="0"
                    />
                  </div>
                </>
              )}

              {/* Turnuva filtreleri */}
              {activeTab === "tournaments" && (
                <>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Turnuva Türü</label>
                    <select
                      value={filters.tournamentType}
                      onChange={(e) => setFilters(f => ({ ...f, tournamentType: e.target.value }))}
                      className="w-full h-10 rounded-lg bg-white/10 border border-white/20 text-white px-3"
                    >
                      <option value="">Tümü</option>
                      <option value="single_elimination">Tek Eleme</option>
                      <option value="double_elimination">Çift Eleme</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Durum</label>
                    <select
                      value={filters.tournamentStatus}
                      onChange={(e) => setFilters(f => ({ ...f, tournamentStatus: e.target.value }))}
                      className="w-full h-10 rounded-lg bg-white/10 border border-white/20 text-white px-3"
                    >
                      <option value="">Tümü</option>
                      <option value="active">Aktif</option>
                      <option value="completed">Tamamlanmış</option>
                    </select>
                  </div>
                </>
              )}

              {/* Sıralama */}
              <div>
                <label className="text-xs text-white/60 mb-1 block">Sıralama</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters(f => ({ ...f, sortBy: e.target.value }))}
                  className="w-full h-10 rounded-lg bg-white/10 border border-white/20 text-white px-3"
                  data-testid="filter-sort"
                >
                  {SORT_OPTIONS[activeTab]?.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sonuçlar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
          </div>
        ) : (
          <>
            {/* Lig sonuçları */}
            {activeTab === "leagues" && (
              <div className="space-y-4" data-testid="league-results">
                {results.leagues?.length > 0 ? (
                  results.leagues.map((league) => (
                    <Link
                      key={league.league_id}
                      to={`/league/${league.league_id}`}
                      className="block glass-card p-4 hover:border-[#4DFFFF]/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#C6A87C]/20 flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-[#C6A87C]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold">{league.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-white/60">
                            <span className="flex items-center gap-1">
                              <Gamepad2 className="w-3 h-3" />
                              {league.game_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {league.players?.length || 0} oyuncu
                            </span>
                            {league.online_enabled && (
                              <span className="text-green-400">● Online</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : searchQuery.length >= 2 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Lig bulunamadı</p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Aramak için en az 2 karakter girin</p>
                  </div>
                )}
              </div>
            )}

            {/* Oyuncu sonuçları */}
            {activeTab === "players" && (
              <div className="space-y-4" data-testid="player-results">
                {results.players?.length > 0 ? (
                  results.players.map((player) => (
                    <Link
                      key={player.user_id}
                      to={`/profile/${player.username}`}
                      className="block glass-card p-4 hover:border-[#4DFFFF]/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                          {player.picture ? (
                            <img src={player.picture} alt={player.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-lg font-bold text-[#4DFFFF]">
                              {player.name?.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold">{player.name}</h3>
                          <p className="text-white/60 text-sm">@{player.username}</p>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-center">
                            <p className="text-[#4DFFFF] font-bold">{player.leagues_count || 0}</p>
                            <p className="text-white/40 text-xs">Lig</p>
                          </div>
                          <div className="text-center">
                            <p className="text-white font-bold">{player.win_rate || 0}%</p>
                            <p className="text-white/40 text-xs">Kazanma</p>
                          </div>
                          {player.badges_count > 0 && (
                            <div className="text-center">
                              <p className="text-[#FFD700] font-bold">{player.badges_count}</p>
                              <p className="text-white/40 text-xs">Rozet</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))
                ) : searchQuery.length >= 2 ? (
                  <div className="text-center py-12">
                    <User className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Oyuncu bulunamadı</p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Aramak için en az 2 karakter girin</p>
                  </div>
                )}
              </div>
            )}

            {/* Turnuva sonuçları */}
            {activeTab === "tournaments" && (
              <div className="space-y-4" data-testid="tournament-results">
                {results.tournaments?.length > 0 ? (
                  results.tournaments.map((tournament) => (
                    <Link
                      key={tournament.tournament_id}
                      to={`/tournament/${tournament.tournament_id}`}
                      className="block glass-card p-4 hover:border-[#4DFFFF]/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                          <Crown className="w-6 h-6 text-[#FFD700]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold">{tournament.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-white/60">
                            <span>{tournament.game_name}</span>
                            <span>
                              {tournament.tournament_type === "single_elimination" ? "Tek Eleme" : "Çift Eleme"}
                            </span>
                            <span>{tournament.players?.length || 0} katılımcı</span>
                          </div>
                        </div>
                        <div className={cn(
                          "px-3 py-1 rounded-full text-xs font-medium",
                          tournament.status === "completed" 
                            ? "bg-green-500/20 text-green-400" 
                            : "bg-yellow-500/20 text-yellow-400"
                        )}>
                          {tournament.status === "completed" ? "Tamamlandı" : "Aktif"}
                        </div>
                      </div>
                    </Link>
                  ))
                ) : searchQuery.length >= 2 || filters.tournamentType ? (
                  <div className="text-center py-12">
                    <Award className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Turnuva bulunamadı</p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Aramak için en az 2 karakter girin</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
